import React from 'react';
import styled from 'styled-components';
import { css } from 'emotion';
import { injectGlobal } from 'react-emotion';
import AniLink from "gatsby-plugin-transition-link/AniLink";
import Row from './Row';

import imgHotels from '../assets/img/audience-hotels.jpg';
import imgCorporates from '../assets/img/audience-corporates.jpg';
import imgWeddings from '../assets/img/audience-weddings.jpg';

/* global tw */

/*
 * "Who it's for" — segmented clarity section.
 * Three audience cards, each led by a real photograph. On scroll-in, a
 * purple curtain wipes up off each photo (glowing edge), the image
 * settle-zooms, and the text rises — staggered card to card. Plays once;
 * falls back to fully-revealed for no-JS / reduced-motion.
 */

injectGlobal`
    @keyframes whoCardFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes whoCurtain  { from { transform: translateY(0); } to { transform: translateY(-101%); } }
    @keyframes whoImg      { from { transform: scale(1.14); } to { transform: scale(1); } }
    @keyframes whoText     { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
`

const Section = styled.section`
    ${tw`w-full relative z-10`};
    background: #0A0A0F;
    padding: 6rem 0 5.5rem;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    @media (max-width: 991px){
        padding: 4rem 0 4rem;
    }
`

const Eyebrow = styled.div`
    ${tw`inline-flex items-center`};
    font-weight: 700;
    font-size: 0.8125rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: #C9A2FF;
    &::before {
        content: '';
        display: inline-block;
        width: 1.6rem;
        height: 2px;
        background: #B15CFF;
        margin-right: 0.7rem;
    }
`

const Title = styled.h2`
    ${tw`m-0`};
    font-weight: 800;
    font-size: 2.25rem;
    line-height: 1.08;
    letter-spacing: -0.035em;
    color: #FFFFFF;
    margin-top: 1rem;
    max-width: 50rem;
    @media (min-width: 992px){
        font-size: 3.5rem;
    }
`

const Cards = styled.div`
    ${tw`flex flex-wrap`};
    width: 100%;
    margin-top: 3.5rem;
    gap: 1.25rem;

    /* The curtain that hides each photo until revealed */
    .curtain {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 2;
        transform: translateY(-101%); /* default / SSR: lifted, photo visible */
        background: linear-gradient(180deg, #0A0A0F 0%, #1A1022 55%, #3A1356 100%);
        pointer-events: none;
    }

    /* ---- armed (JS mounted, pre-play) ---- */
    &.armed .card { opacity: 0; }
    &.armed .curtain { transform: translateY(0); }
    &.armed .who-img { transform: scale(1.14); }
    &.armed .reveal-text { opacity: 0; transform: translateY(14px); }

    /* ---- in-view: play, staggered by --d ---- */
    &.armed.in-view .card {
        animation: whoCardFade 0.7s ease both;
        animation-delay: var(--d);
    }
    &.armed.in-view .curtain {
        animation: whoCurtain 0.85s cubic-bezier(.66, 0, .2, 1) both;
        animation-delay: calc(var(--d) + 0.18s);
    }
    &.armed.in-view .who-img {
        animation: whoImg 1.7s ease-out both;
        animation-delay: calc(var(--d) + 0.18s);
    }
    &.armed.in-view .reveal-text {
        animation: whoText 0.6s ease-out both;
        animation-delay: calc(var(--d) + 0.5s);
    }

    @media (prefers-reduced-motion: reduce) {
        &.armed .card,
        &.armed .reveal-text { opacity: 1 !important; transform: none !important; animation: none !important; }
        &.armed .curtain { transform: translateY(-101%) !important; animation: none !important; }
        &.armed .who-img { transform: scale(1) !important; animation: none !important; }
    }
`

const Card = styled.div`
    ${tw`flex flex-col`};
    flex: 1 1 18rem;
    min-width: 0;
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 18px;
    overflow: hidden;
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
    &:hover {
        transform: translateY(-4px);
        border-color: rgba(177, 92, 255, 0.5);
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
    }
`

const ImageFrame = styled.div`
    ${tw`relative w-full`};
    height: 14rem;
    overflow: hidden;
    .who-img {
        ${tw`absolute w-full h-full`};
        top: 0;
        left: 0;
        object-fit: cover;
    }
    &::after {
        content: '';
        ${tw`absolute w-full`};
        left: 0;
        bottom: 0;
        height: 55%;
        background: linear-gradient(to bottom, rgba(10,10,15,0) 0%, rgba(10,10,15,0.85) 100%);
        z-index: 1;
    }
`

const Body = styled.div`
    ${tw`flex flex-col`};
    flex: 1;
    padding: 1.75rem 1.75rem 1.75rem;
    h3 {
        ${tw`m-0`};
        font-weight: 700;
        font-size: 1.5rem;
        letter-spacing: -0.02em;
        color: #FFFFFF;
    }
    p {
        font-weight: 500;
        font-size: 1.0625rem;
        line-height: 1.55;
        color: rgba(255, 255, 255, 0.68);
        margin-top: 0.6rem;
        margin-bottom: 0;
    }
`

const ExploreLink = styled(AniLink)`
    ${tw`inline-flex items-center no-underline`};
    margin-top: auto;
    padding-top: 1.75rem;
    font-weight: 700;
    font-size: 0.9375rem;
    letter-spacing: -0.005em;
    color: #C9A2FF;
    transition: color 0.2s ease;
    &::after {
        content: '→';
        margin-left: 0.45rem;
        transition: transform 0.2s ease;
    }
    &:hover {
        color: #FFFFFF;
    }
    &:hover::after {
        transform: translateX(4px);
    }
`

const AUDIENCES = [
    {
        img: imgCorporates,
        title: 'Executive Travel',
        copy: 'Punctual airport transfers and city travel for leaders who run on tight schedules.',
    },
    {
        img: imgWeddings,
        title: 'Weddings & Celebrations',
        copy: 'Effortless travel for your family and guests across every venue and ritual.',
    },
    {
        img: imgHotels,
        title: 'Outstation Getaways',
        copy: 'Chauffeur-driven journeys to the hills, the coast, or the city next door.',
    },
];

class SectionWhoFor extends React.Component {
    constructor(props) {
        super(props);
        this.state = { armed: false, inView: false };
        this.ref = React.createRef();
    }

    componentDidMount() {
        this.setState({ armed: true });
        const node = this.ref.current;
        if (!node || typeof IntersectionObserver === 'undefined') {
            this.setState({ inView: true });
            return;
        }
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
                    this.setState({ inView: true });
                    if (this.observer) { this.observer.disconnect(); }
                }
            });
        }, { threshold: [0, 0.2] });
        this.observer.observe(node);
    }

    componentWillUnmount() {
        if (this.observer) { this.observer.disconnect(); }
    }

    render() {
        const cls = `${this.state.armed ? 'armed' : ''} ${this.state.inView ? 'in-view' : ''}`.trim();
        return (
            <Section>
                <Row>
                    <div className={css`${tw`w-full`}`}>
                        <Eyebrow>Who it's for</Eyebrow>
                        <Title>Designed for Those Who Value Experience</Title>
                        <Cards ref={this.ref} className={cls}>
                            {AUDIENCES.map((item, i) => (
                                <Card key={item.title} style={{ '--d': `${i * 0.18}s` }}>
                                    <ImageFrame>
                                        <img className="who-img" src={item.img} alt={item.title} />
                                        <span className="curtain" />
                                    </ImageFrame>
                                    <Body>
                                        <h3 className="reveal-text">{item.title}</h3>
                                        <p className="reveal-text">{item.copy}</p>
                                        <ExploreLink className="reveal-text" to="/concierge/" hex="#8B3DF5">
                                            Explore Solutions
                                        </ExploreLink>
                                    </Body>
                                </Card>
                            ))}
                        </Cards>
                    </div>
                </Row>
            </Section>
        );
    }
}

export default SectionWhoFor;
